import { DataProvider, fetchUtils } from 'react-admin';
import { stringify } from 'query-string';
import { tenantService } from '../services/tenantService';
import ResourceFilter from './ResourceFilter';

const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

// CRM resources
export const legacyResources = ['users/search', 'chat-channels', 'chat-messages/search', 'timetracking/segments'];

// Helper function to build query parameters for legacy resources ported from EXT
const buildLegacyQueryParams = (resource: string, params: any) => {
  const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
  const { field, order } = params.sort || { field: 'id', order: 'DESC' };

  const query = {
    page: page,
    perPage: perPage,
    orderByField: field,
    order: order.toLowerCase(),
    where: createWhere(resource, params),
    ...params.filter.extra,
    ...params.meta,
  };

  if (resource === 'chat-channels') {
    query.includeLastMessage = params.filter.includeLastMessage;
  }

  return query;
};

const createWhere = (resource: string, params: any): string => {
  return new ResourceFilter(resource, params).stringified;
};

// HTTP client with authentication and tenant headers
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  // Only set Content-Type if not using FormData
  if (
    !(options.body instanceof FormData) &&
    options.method !== 'GET' &&
    options.method !== 'DELETE'
  ) {
    options.headers.set('Content-Type', 'application/json');
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Add tenant code header (similar to fortress-web approach)
  try {
    const tenantCode = tenantService.getCurrentTenantCode();
    options.headers.set('x-tenant-code', tenantCode);
  } catch (error) {
    throw new Error('No tenant selected. Please select a tenant to continue.');
  }

  try {
    const response = await fetchUtils.fetchJson(url, options);
    return response;
  } catch (error: any) {
    if (error.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
};

export const legacyDataProvider: DataProvider = {
  getList: async (resource, params) => {
    const query = buildLegacyQueryParams(resource, params);
    const url = `${baseUrl}/${resource}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching legacy ${resource} list:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url);
      return { data: json };
    } catch (error) {
      console.error(`Error fetching legacy ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    try {
      const promises = params.ids.map(id =>
        httpClient(`${baseUrl}/${resource}/${id}`)
          .then(({ json }) => json)
          .catch(() => null)
      );

      const results = await Promise.all(promises);
      const data = results.filter(item => item !== null);
      return { data };
    } catch (error) {
      console.error(`Error fetching multiple legacy ${resource}:`, error);
      return { data: [] };
    }
  },

  getManyReference: async (resource, params) => {
    const query = buildLegacyQueryParams(resource, params);
    query[params.target] = params.id;

    const url = `${baseUrl}/${resource}?${stringify(query)}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (json.data && json.metadata) {
        data = json.data;
        total = json.metadata.total;
      } else if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching legacy ${resource} references:`, error);
      return { data: [], total: 0 };
    }
  },

  create: async (resource, params) => {
    const url = `${baseUrl}/${resource}`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });

      return {
        data: { ...params.data, ...json },
      };
    } catch (error) {
      console.error(`Error creating legacy ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      });

      return { data: json };
    } catch (error) {
      console.error(`Error updating legacy ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/${resource}/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple legacy ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const url = `${baseUrl}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });

      return { data: json };
    } catch (error) {
      console.error(`Error deleting legacy ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/${resource}/${id}`, {
            method: 'DELETE',
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple legacy ${resource}:`, error);
      throw error;
    }
  },
};
